import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Button,
  Skeleton,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { MdArrowBack, MdContentCopy, MdInventory2 } from "react-icons/md";

import { Surface, StatusChip, ConfirmDialog } from "components/ui";
import { T } from "theme/tokens";

import * as api from "./api";
import OverviewTab from "./tabs/OverviewTab";
import OrdersTab from "./tabs/OrdersTab";
import ProfileTab from "./tabs/ProfileTab";
import BankTab from "./tabs/BankTab";
import LocationTab from "./tabs/LocationTab";
import DocumentsTab from "./tabs/DocumentsTab";
import WalletTab from "./tabs/WalletTab";
import StatusTab from "./tabs/StatusTab";
import ActivityTab from "./tabs/ActivityTab";

const TABS = [
  { key: "overview", label: "Overview", Component: OverviewTab },
  { key: "orders", label: "Orders", Component: OrdersTab },
  { key: "profile", label: "Profile & KYC", Component: ProfileTab },
  { key: "bank", label: "Bank", Component: BankTab },
  { key: "wallet", label: "Wallet", Component: WalletTab },
  { key: "location", label: "Location", Component: LocationTab },
  { key: "documents", label: "Documents", Component: DocumentsTab },
  { key: "status", label: "Status", Component: StatusTab },
  { key: "activity", label: "Activity", Component: ActivityTab },
];

export default function PharmacyDetail() {
  const { pharmacyId } = useParams();
  const navigate = useNavigate();

  const [pharmacy, setPharmacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState("");

  // Turning order-taking off stops the pharmacy receiving work, so it is
  // confirmed with a reason rather than fired straight from the switch.
  const [pendingSwitch, setPendingSwitch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.getPharmacy(pharmacyId);
      setPharmacy(data.pharmacy);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load this pharmacy."
      );
    } finally {
      setLoading(false);
    }
  }, [pharmacyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleVerification = async (next, reason) => {
    await api.setVerification(pharmacyId, next, reason);
    setPharmacy((p) => ({ ...p, verifiedPharamacy: next }));
    setToast(next ? "Pharmacy approved." : "Pharmacy rejected.");
  };

  const handleTakingOrders = async (next, reason) => {
    await api.setTakingOrders(pharmacyId, next, reason);
    setPharmacy((p) => ({ ...p, isTakingOrders: next }));
    setToast(next ? "Now taking orders." : "Order-taking paused.");
  };

  const copyId = () => {
    navigator.clipboard?.writeText(pharmacy?.pharmacyId || pharmacyId);
    setToast("Pharmacy ID copied.");
  };

  if (loading && !pharmacy) {
    return (
      <Box sx={{ pt: 5 }}>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3, mb: 2 }} />
        <Skeleton variant="rounded" height={480} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error && !pharmacy) {
    return (
      <Box sx={{ pt: 5 }}>
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  const ActiveTab = TABS[tab].Component;

  return (
    <Box sx={{ pt: 5 }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Surface sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Button
              size="small"
              onClick={() => navigate("/admin/pharmacy-partners")}
              sx={{ minWidth: 0, color: T.muted, mt: 0.3 }}
            >
              <MdArrowBack size={20} />
            </Button>

            <Box>
              <Typography
                sx={{ fontSize: "1.25rem", fontWeight: 700, color: T.ink, lineHeight: 1.2 }}
              >
                {pharmacy?.bussinessName || "Unnamed pharmacy"}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.4 }}>
                <Typography
                  sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: T.muted }}
                >
                  {pharmacy?.medstownid || pharmacy?.pharmacyId}
                </Typography>
                <Tooltip title="Copy pharmacy ID">
                  <Button
                    size="small"
                    onClick={copyId}
                    sx={{ minWidth: 0, p: 0.3, color: T.subtle }}
                  >
                    <MdContentCopy size={14} />
                  </Button>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={0.8} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <StatusChip
                  status={pharmacy?.verifiedPharamacy ? "verified" : "pending"}
                  label={pharmacy?.verifiedPharamacy ? "Verified" : "Not verified"}
                />
                <StatusChip
                  status={pharmacy?.isTakingOrders ? "accepted" : "pending"}
                  label={pharmacy?.isTakingOrders ? "Taking orders" : "Paused"}
                />
                {pharmacy?.isLoggedIn && (
                  <StatusChip status="active" label="Logged in" />
                )}
                {pharmacy?.isDeleted && (
                  <StatusChip status="cancelled" label="Deleted" />
                )}
              </Stack>
            </Box>
          </Stack>

          {/* The two switches ops reach for most, without opening a tab. */}
          <Stack spacing={0.5} alignItems={{ xs: "flex-start", md: "flex-end" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(pharmacy?.verifiedPharamacy)}
                  disabled={pharmacy?.isDeleted}
                  onChange={(e) =>
                    setPendingSwitch({ kind: "verification", next: e.target.checked })
                  }
                />
              }
              label={
                <Typography sx={{ fontSize: "0.82rem", color: T.muted }}>
                  Approved
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(pharmacy?.isTakingOrders)}
                  disabled={pharmacy?.isDeleted}
                  onChange={(e) =>
                    setPendingSwitch({ kind: "takingOrders", next: e.target.checked })
                  }
                />
              }
              label={
                <Typography sx={{ fontSize: "0.82rem", color: T.muted }}>
                  Taking orders
                </Typography>
              }
            />
            <Button
              size="small"
              startIcon={<MdInventory2 />}
              onClick={() => navigate(`/admin/pharmacy-inventory/${pharmacyId}`)}
              sx={{ textTransform: "none", color: T.secondary }}
            >
              Inventory
            </Button>
          </Stack>
        </Stack>
      </Surface>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <Surface sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              color: T.muted,
              minHeight: 48,
            },
            "& .Mui-selected": { color: `${T.primary} !important` },
            "& .MuiTabs-indicator": { backgroundColor: T.primary, height: 3 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      </Surface>

      <ActiveTab
        pharmacyId={pharmacyId}
        pharmacy={pharmacy}
        onChanged={load}
        notify={setToast}
      />

      {/* ── Switch confirmation ────────────────────────────────────────── */}
      <ConfirmDialog
        open={Boolean(pendingSwitch)}
        title={
          pendingSwitch?.kind === "verification"
            ? pendingSwitch?.next
              ? "Approve this pharmacy?"
              : "Reject this pharmacy?"
            : pendingSwitch?.next
            ? "Resume order-taking?"
            : "Pause order-taking?"
        }
        description={
          pendingSwitch?.kind === "verification"
            ? pendingSwitch?.next
              ? "It will start receiving customer orders."
              : "It will stop receiving orders and be hidden from customers."
            : pendingSwitch?.next
            ? "New orders will be routed to this pharmacy again."
            : "It stays verified, but new orders will go elsewhere."
        }
        confirmLabel={pendingSwitch?.next ? "Enable" : "Disable"}
        destructive={pendingSwitch ? !pendingSwitch.next : false}
        // Turning something off is what someone will later ask about.
        requireReason={pendingSwitch ? !pendingSwitch.next : false}
        onConfirm={async (reason) => {
          if (pendingSwitch.kind === "verification") {
            await handleVerification(pendingSwitch.next, reason);
          } else {
            await handleTakingOrders(pendingSwitch.next, reason);
          }
        }}
        onClose={() => setPendingSwitch(null)}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
}
